import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Play, Pause, RotateCcw, ChevronLeft, ChevronRight, Gauge, Maximize2, Minimize2, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/context/LanguageContext";
import { ReadingSurface } from "@/components/reader/ReadingSurface";
import { ReaderSettings } from "@/components/reader/ReaderSettings";
import { HighlightsOverview } from "@/components/reader/HighlightsOverview";
import { useReaderSettings } from "@/components/reader/readerPrefs";
import { useSmoothFollow, prefersReducedMotion } from "@/components/reader/readerScroll";
import { createTrail, TRAIL_MS, TRAIL_CAP, TRAIL_COLOR } from "@/components/reader/readerTrail";
import {
    buildDocModel, buildUnits, unitIndexForToken, buildHighlightIndex, DEFAULT_LAYERS, expandHighlightSpan,
} from "@/components/reader/readingEngine";
import { SelectionExplainLayer } from "@/components/reader/useSelectionExplain";

const PREFS_KEY = "reading_os_prefs";
const loadPrefs = () => { try { return JSON.parse(localStorage.getItem(PREFS_KEY) || "{}") || {}; } catch (e) { return {}; } };
const savePref = (patch) => { try { localStorage.setItem(PREFS_KEY, JSON.stringify({ ...loadPrefs(), ...patch })); } catch (e) {} };

const GRAINS = ["word", "phrase", "sentence"];

/**
 * Guided Reading — the whole document stays visible and normally lit. Only the
 * ACTIVE word/phrase changes COLOUR (a calm karaoke trail, no box), advancing at
 * the chosen WPM. The page scrolls CONTINUOUSLY (single rAF loop) to keep the
 * active line inside a stable reading focus zone — never a page jump. Built on
 * the shared reading engine + ReadingSurface so Smart Highlights and Word Anchor
 * plug in without a redesign. Best for pace + flow.
 */
export const GuidedStage = ({
    text = "",
    total = 0,
    initialIndex = 0,
    seekTo,
    onProgress,
    onSession,
    onComplete,
    highlights,
    onExplain,
}) => {
    const { t } = useTranslation();
    const rs = useReaderSettings();
    const model = useMemo(() => buildDocModel(text), [text]);
    const ratio = total > 0 ? total / model.totalTokens : 1;

    const [granularity, setGranularity] = useState(() => {
        const g = loadPrefs().guidedGranularity;
        return GRAINS.includes(g) ? g : "phrase";
    });
    const units = useMemo(() => buildUnits(model, granularity), [model, granularity]);

    const [wpm, setWpm] = useState(() => loadPrefs().wpm || 350);
    const [playing, setPlaying] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);

    const initialUnit = useMemo(() => {
        const startTok = Math.round((total > 0 ? initialIndex / total : 0) * model.totalTokens);
        return unitIndexForToken(units, startTok);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [active, setActive] = useState(initialUnit);

    const containerRef = useRef(null);
    const surfaceRef = useRef(null);
    const headRef = useRef(null);
    const timerRef = useRef(null);
    const activeRef = useRef(active);
    const unitsRef = useRef(units);
    const wpmRef = useRef(wpm);
    const immediateScrollRef = useRef(false);
    const sessionRef = useRef({ startTime: null, startWord: 0 });

    const reducedMotion = useMemo(() => prefersReducedMotion(), []);
    const { follow, stop } = useSmoothFollow(containerRef, { reducedMotion });

    // Light-trail engine (WAAPI, no rAF, no re-render). Refs so applyActive stays stable.
    const trailRef = useRef(null);
    if (!trailRef.current) trailRef.current = createTrail();
    const focusModeRef = useRef(rs.guidedFocus);
    const trailLenRef = useRef(rs.trailLength);
    const trailColorRef = useRef(TRAIL_COLOR[rs.readerTheme] || TRAIL_COLOR.night);
    useEffect(() => { focusModeRef.current = rs.guidedFocus; }, [rs.guidedFocus]);
    useEffect(() => { trailLenRef.current = rs.trailLength; }, [rs.trailLength]);
    useEffect(() => { trailColorRef.current = TRAIL_COLOR[rs.readerTheme] || TRAIL_COLOR.night; }, [rs.readerTheme]);
    useEffect(() => () => trailRef.current?.clear(), []);

    useEffect(() => { activeRef.current = active; }, [active]);
    useEffect(() => { unitsRef.current = units; }, [units]);
    useEffect(() => { wpmRef.current = wpm; }, [wpm]);

    const enableHighlights = !!(highlights?.enabled && highlights?.data);
    const highlightIndex = useMemo(() => {
        if (!enableHighlights) return null;
        const eff = highlights.layers || DEFAULT_LAYERS;
        return buildHighlightIndex(model, highlights.data, eff);
    }, [enableHighlights, model, highlights?.data, highlights?.layers]);

    // Continuously glide the head hotspot to the active word's first-line rect.
    const positionHead = useCallback((els, u, immediate) => {
        const headEl = headRef.current;
        if (!headEl || !els.length) return;
        const f = els[0];
        const top0 = f.offsetTop;
        let left = f.offsetLeft;
        let right = f.offsetLeft + f.offsetWidth;
        let h = f.offsetHeight;
        for (const el of els) {
            if (el.offsetTop !== top0) break; // first line only
            left = Math.min(left, el.offsetLeft);
            right = Math.max(right, el.offsetLeft + el.offsetWidth);
            h = Math.max(h, el.offsetHeight);
        }
        const dwell = Math.max(40, (Math.max(1, u.wordCount) / Math.max(60, wpmRef.current)) * 60000);
        const glide = Math.round(dwell); // = dwell → hotspot never stops between words
        headEl.style.background = trailColorRef.current.hot;
        headEl.style.setProperty("--rg-head-ms", `${glide}ms`);
        if (immediate) {
            headEl.style.transition = "none";
            headEl.style.width = `${right - left}px`;
            headEl.style.height = `${h}px`;
            headEl.style.transform = `translate3d(${left}px, ${top0}px, 0)`;
            void headEl.offsetWidth; // commit without animating
            headEl.style.transition = "";
        } else {
            headEl.style.width = `${right - left}px`;
            headEl.style.height = `${h}px`;
            headEl.style.transform = `translate3d(${left}px, ${top0}px, 0)`;
        }
        headEl.classList.add("rg-head-on");
    }, []);

    const hideHead = useCallback(() => { headRef.current?.classList.remove("rg-head-on"); }, []);

    // Apply the moving guide via DOM class toggles / per-token trail + gliding head
    // (no token re-render => smooth). "word" = colour the active unit; "trail" =
    // light-trail comet with a gliding hotspot; "minimal" = no marker, scroll only.
    const applyActive = useCallback((doScroll) => {
        const surf = surfaceRef.current;
        if (!surf) return;
        const u = unitsRef.current[activeRef.current];
        if (!u) return;
        const immediate = immediateScrollRef.current;

        const els = [];
        let first = null;
        for (let i = u.start; i <= u.end; i++) {
            const n = surf.querySelector(`[data-tok="${i}"]`);
            if (n) { els.push(n); if (!first) first = n; }
        }

        const mode = focusModeRef.current;
        if (mode === "word") {
            surf.querySelectorAll(".rg-active").forEach((n) => n.classList.remove("rg-active"));
            els.forEach((n) => n.classList.add("rg-active"));
            hideHead();
        } else if (mode === "trail") {
            const c = trailColorRef.current;
            trailRef.current.setHead(els, {
                immediate,
                headColor: c.base,
                zero: c.zero,
                T: TRAIL_MS[trailLenRef.current] || TRAIL_MS.medium,
                cap: TRAIL_CAP[trailLenRef.current] || TRAIL_CAP.medium,
            });
            positionHead(els, u, immediate);
        } else {
            hideHead(); // minimal
        }

        if (doScroll && first && rs.autoScroll) {
            follow(first, { immediate });
        }
        immediateScrollRef.current = false;
    }, [follow, rs.autoScroll, positionHead, hideHead]);

    useEffect(() => { applyActive(true); }, [active, applyActive]);
    // Re-apply after the surface re-renders or the guide style / typography / theme
    // changes — wipe any stale trail/active state first, snap (no glide), no scroll.
    useEffect(() => {
        const surf = surfaceRef.current;
        if (surf) surf.querySelectorAll(".rg-active").forEach((n) => n.classList.remove("rg-active"));
        trailRef.current?.clear();
        hideHead();
        immediateScrollRef.current = true;
        applyActive(false);
    }, [highlightIndex, units, rs.surface.anchorMode, rs.guidedFocus, rs.trailLength, rs.readerTheme, rs.surface.textAlign, rs.surface.fontSize, rs.surface.maxWidth, rs.surface.lineHeight, rs.surface.fontFamily, applyActive, hideHead]);

    // External seek (chat jump-to-source) — center immediately.
    useEffect(() => {
        if (!seekTo || typeof seekTo.index !== "number") return;
        setPlaying(false);
        immediateScrollRef.current = true;
        const tok = Math.round((total > 0 ? seekTo.index / total : 0) * model.totalTokens);
        setActive(unitIndexForToken(units, tok));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seekTo?.ts]);

    // Progress reporting.
    useEffect(() => {
        const u = units[active];
        if (!u) return;
        const idx = Math.round(u.start * ratio);
        const pct = total ? (idx / total) * 100 : 0;
        onProgress?.(idx, pct);
    }, [active, units, ratio, total, onProgress]);

    // Auto-advance loop.
    useEffect(() => {
        if (!playing) return;
        if (active >= units.length - 1) { setPlaying(false); onComplete?.(); return; }
        const u = units[active];
        const delay = Math.max(40, (Math.max(1, u.wordCount) / Math.max(60, wpm)) * 60000);
        timerRef.current = setTimeout(() => setActive((a) => Math.min(units.length - 1, a + 1)), delay);
        return () => clearTimeout(timerRef.current);
    }, [playing, active, wpm, units, onComplete]);

    // Session tracking (attributed to the guided mode).
    const flushSession = useCallback(() => {
        const s = sessionRef.current;
        if (s.startTime == null) return;
        const dur = (Date.now() - s.startTime) / 1000;
        const curWord = unitsRef.current[activeRef.current]?.start ?? s.startWord;
        const wr = Math.max(0, Math.round((curWord - s.startWord) * ratio));
        s.startTime = null;
        if (dur > 1 && wr > 0) onSession?.({ wordsRead: wr, durationSec: dur, wpm, mode: "guided" });
    }, [onSession, wpm, ratio]);

    useEffect(() => {
        if (playing) sessionRef.current = { startTime: Date.now(), startWord: units[active]?.start ?? 0 };
        else { flushSession(); stop(); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playing]);
    useEffect(() => () => flushSession(), [flushSession]);

    useEffect(() => { savePref({ wpm, guidedGranularity: granularity }); }, [wpm, granularity]);

    const togglePlay = useCallback(() => {
        setActive((a) => (a >= units.length - 1 ? 0 : a));
        setPlaying((p) => !p);
    }, [units.length]);
    const prev = useCallback(() => setActive((a) => Math.max(0, a - 1)), []);
    const next = useCallback(() => setActive((a) => Math.min(units.length - 1, a + 1)), [units.length]);
    const restart = useCallback(() => { setPlaying(false); stop(); setActive(0); if (containerRef.current) containerRef.current.scrollTo({ top: 0, behavior: reducedMotion ? "auto" : "smooth" }); }, [stop, reducedMotion]);

    const onTokenClick = useCallback((i) => {
        setActive(unitIndexForToken(unitsRef.current, i));
    }, []);

    // Jump to a specific token (Smart Highlights overview) — pause + center now.
    const jumpToToken = useCallback((tok) => {
        setPlaying(false);
        immediateScrollRef.current = true;
        setActive(unitIndexForToken(unitsRef.current, tok));
    }, []);

    const explainToken = useCallback((tokenIndex, e) => {
        if (!onExplain) return;
        const target = expandHighlightSpan(model, highlightIndex, tokenIndex);
        if (!target?.term) return;
        setPlaying(false);
        onExplain(target.term, target.context, e.currentTarget.getBoundingClientRect(), Math.round(tokenIndex * ratio));
    }, [onExplain, model, highlightIndex, ratio]);

    const changeGranularity = useCallback((g) => {
        setActive((a) => {
            const curTok = unitsRef.current[a]?.start ?? 0;
            const nextUnits = buildUnits(model, g);
            return unitIndexForToken(nextUnits, curTok);
        });
        setGranularity(g);
    }, [model]);

    useEffect(() => {
        const onKey = (e) => {
            if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
            if (e.code === "Space") { e.preventDefault(); togglePlay(); }
            else if (e.code === "ArrowLeft") { e.preventDefault(); prev(); }
            else if (e.code === "ArrowRight") { e.preventDefault(); next(); }
            else if (e.key === "f" || e.key === "F") setFullscreen((v) => !v);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [togglePlay, prev, next]);

    const pct = units.length ? Math.round(((active + 1) / units.length) * 100) : 0;

    return (
        <div className={cn("h-full", fullscreen ? "fixed inset-0 z-50 bg-surface-0 p-3 sm:p-6" : "")} data-testid="guided-stage">
            <div className="relative flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-surface-1">
                {/* Top bar */}
                <div className="relative z-10 flex flex-wrap items-center justify-between gap-2 border-b border-border/60 px-4 py-3">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 mono-label text-t3">
                            <Wand2 className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> {t("reader.modes.guided.label")}
                        </div>
                        <p className="mt-0.5 hidden text-xs text-t4 sm:block">{t("rset.guidedDesc")}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <ReaderSettings
                            settings={rs}
                            hl={highlights}
                            movement={{ granularity, setGranularity: changeGranularity, grains: GRAINS }}
                            guided={{ focus: rs.guidedFocus, setFocus: rs.setGuidedFocus, trailLength: rs.trailLength, setTrailLength: rs.setTrailLength }}
                        />
                        {enableHighlights && highlights?.data && (
                            <HighlightsOverview
                                model={model}
                                data={highlights.data}
                                layers={highlights.layers}
                                ratio={ratio}
                                onJump={jumpToToken}
                                onExplain={onExplain}
                            />
                        )}
                        <Button data-testid="guided-fullscreen-toggle" variant="ghost" size="icon" className="text-t3" onClick={() => setFullscreen((v) => !v)}>
                            {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                        </Button>
                    </div>
                </div>

                {/* Reading surface — text is fixed; only the guide colour moves. */}
                <div
                    ref={containerRef}
                    className={cn("rg-reader relative flex-1 overflow-y-auto px-6 py-10 sm:px-10", `rg-theme-${rs.readerTheme}`)}
                    data-testid="guided-scroll-container"
                >
                    <ReadingSurface
                        model={model}
                        surfaceRef={surfaceRef}
                        headLayer
                        headRef={headRef}
                        highlightIndex={highlightIndex}
                        enableHighlights={enableHighlights}
                        onTokenClick={onTokenClick}
                        onExplain={onExplain ? explainToken : undefined}
                        anchorMode={rs.surface.anchorMode}
                        anchorIntensity={rs.surface.anchorIntensity}
                        fontFamily={rs.surface.fontFamily}
                        fontSize={rs.surface.fontSize}
                        maxWidth={rs.surface.maxWidth}
                        lineHeight={rs.surface.lineHeight}
                        textAlign={rs.surface.textAlign}
                        testid="guided-surface"
                    />
                    {onExplain && <SelectionExplainLayer containerRef={containerRef} onExplain={onExplain} ratio={ratio} />}
                </div>

                {/* Controls dock */}
                <div className="relative z-10 border-t border-border bg-surface-1/80 px-4 py-3 backdrop-blur-md">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-1.5">
                            <Button data-testid="guided-restart-button" variant="ghost" size="icon" className="text-t3" onClick={restart}><RotateCcw className="h-4 w-4" /></Button>
                            <Button data-testid="guided-prev-button" variant="ghost" size="icon" className="text-t2" onClick={prev}><ChevronLeft className="h-5 w-5" /></Button>
                            <Button data-testid="guided-play-pause-button" size="icon" className="h-11 w-11 rounded-full" onClick={togglePlay}>
                                {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                            </Button>
                            <Button data-testid="guided-next-button" variant="ghost" size="icon" className="text-t2" onClick={next}><ChevronRight className="h-5 w-5" /></Button>
                        </div>
                        <div className="flex items-center gap-3">
                            <Gauge className="h-4 w-4 text-t4" />
                            <div className="w-28"><Slider data-testid="guided-wpm-slider" value={[wpm]} min={100} max={1500} step={25} onValueChange={(v) => setWpm(v[0])} /></div>
                            <span className="w-16 font-mono-ui text-xs text-t2">{wpm} wpm</span>
                        </div>
                    </div>
                    <div className="mt-1.5 flex items-center justify-between font-mono-ui text-[11px] text-t4">
                        <span>{pct}%</span>
                        <span>{t(`rset.grain.${granularity}`)} {Math.min(active + 1, units.length)} / {units.length}</span>
                        <span className="hidden sm:inline">Space · ← → · F</span>
                    </div>
                </div>
            </div>
        </div>
    );
};
